import React, { useState, useContext, useEffect } from 'react'
import { Autocomplete, Button, Dialog, DialogContent, Divider, TextField } from '@mui/material'

import LoadingIndicator from 'src/common/components/LoadingIndicator'
import settingsService from 'src/services/settings.service'

import { SnackBarContext } from 'src/App'
import { ConnectorIcon } from './integrations'
import Spacer from 'src/common/components/Spacer'
import CustomCheckbox from 'src/common/components/CustomCheckbox'
import CyberSwitchV2 from 'src/common/components/SwitchV2'
import RadioSelect from 'src/common/components/RadioSelect'
// import { toggleElement } from 'src/heplers'

const INCLUDE_TYPES = {
  allUsers: 'ALL_USERS',
  allUsersAndGroups: 'ALL_USERS_AND_GROUPS',
  onlySpecificUsers: 'ONLY_SPECIFIC_USERS'
}

const syncBasis = {
  Daily: 'EVERY_DAY',
  'Three-day': 'THREE_DAYS',
  Weekly: 'ONE_WEEK',
  Monthly: 'ONE_MONTH'
}

const ConfigureDialog = ({
  open,
  onClose,
  type = '',
  handleConnectTo,
  onDisconnect,
  connectors,
  onSuccess,
  // cooldownConnector,
  startCooldown,
  setCooldownConnectors,
  cooldownConnectors,
  onUpdateCredentials
}) => {
  const { setSuccessMessage, setErrorMessage } = useContext(SnackBarContext)

  const [loading, setLoading] = useState(false)
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false)
  const [autoSyncBasis, setAutoSyncBasis] = useState('Daily')
  const [includeType, setIncludeType] = useState(null)
  const [groups, setGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [selectedOptions, setSelectedOptions] = useState([])

  const [cooldown, setCooldown] = useState({ time: 0, loading: false })

  const isCooldown = Boolean(cooldown.time > 0)

  const changeSyncBasis = e => {
    setAutoSyncBasis(e)
  }

  useEffect(() => {
    if (type && open) {
      setCooldown(
        cooldownConnectors && cooldownConnectors[type] ? cooldownConnectors[type] : { time: 0, loading: false }
      )
    }
  }, [type, cooldownConnectors, open])

  function findKeyByValue(obj, value) {
    return Object.keys(obj).find(key => obj[key] === value)
  }

  useEffect(() => {
    if (!open || !type || !connectors || connectors.length === 0) {
      return
    }
    setLoading(true)

    const handleAD = groupsResp => {
      settingsService
        .getInfo(type)
        .then(resp => {
          const actualGroupsAd = resp.data.groups
            .map(el => el.uuid)
            .filter(item => groupsResp.data.map(gr => gr.uuid).includes(item))
          setAutoSyncEnabled(resp.data.autoSync)
          setAutoSyncBasis(findKeyByValue(syncBasis, resp.data.syncFrequency))
          setSelectedGroups(actualGroupsAd)
          setSelectedOptions(resp.data.groups.map(el => ({ name: el.name, uuid: el.uuid })))
          const validSyncTypes = Object.values(INCLUDE_TYPES)
          const syncTypeToSetForAD =
            resp.data.syncType && validSyncTypes.includes(resp.data.syncType)
              ? resp.data.syncType
              : INCLUDE_TYPES.allUsers
          setIncludeType(syncTypeToSetForAD)
        })
        .catch(e => {
          console.error(e)
          setErrorMessage('Error fetching data')
          setIncludeType(INCLUDE_TYPES.allUsers)
        })
    }

    const handleOther = groupsResp => {
      const currentConnector = connectors.find(el => el.provider == type)
      if (currentConnector) {
        const actualGroups = currentConnector.externalGroups
          .map(el => el.uuid)
          .filter(item => groupsResp.data.map(gr => gr.uuid).includes(item))
        setAutoSyncEnabled(currentConnector.autoSync)
        setAutoSyncBasis(findKeyByValue(syncBasis, currentConnector.syncFrequency))
        setSelectedGroups(actualGroups)
        setSelectedOptions(currentConnector.externalGroups.map(el => ({ name: el.name, uuid: el.uuid })))
        const validSyncTypes = Object.values(INCLUDE_TYPES)
        const syncTypeToSet =
          currentConnector.syncType && validSyncTypes.includes(currentConnector.syncType)
            ? currentConnector.syncType
            : INCLUDE_TYPES.allUsers
        setIncludeType(syncTypeToSet)
      } else {
        setIncludeType(INCLUDE_TYPES.allUsers)
      }
    }

    settingsService
      .getGroups(type)
      .then(groupsResp => {
        setGroups(groupsResp.data)
        if (type == 'AD') {
          handleAD(groupsResp)
        } else {
          handleOther(groupsResp)
        }
      })
      .catch(e => {
        console.error(e)
        setErrorMessage('Error fetching groups')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [open, connectors])

  const clearForm = () => {
    onClose()
    setSelectedGroups([])
  }

  const handleSave = () => {
    setLoading(true)
    startCooldown(type)
    const body = {
      groups:
        includeType == INCLUDE_TYPES.onlySpecificUsers ? groups.filter(el => selectedGroups.includes(el.uuid)) : [],
      autoSync: autoSyncEnabled,
      syncFrequency: syncBasis[autoSyncBasis],
      provider: type,
      syncType: includeType
    }

    settingsService
      .setSyncUsersConfig(body)
      .then(() => {
        settingsService
          .sync(type)
          .then(() => {
            if (typeof onSuccess === 'function') {
              onSuccess()
            }
            setSuccessMessage('Users and groups successfully synchronized')
            clearForm()
          })
          .catch(err => {
            const message =
              err?.response?.data?.description ||
              err.data?.description ||
              err.error ||
              'An error occurred during the operation'
            setErrorMessage(message)
          })
          .finally(() => {
            setLoading(false)
          })
      })
      .catch(() => {
        setErrorMessage('Sync failed From SetSyncUsersConfig Error')
        setCooldownConnectors(prev => ({ ...prev, [type]: null }))
      })
  }

  const disconnect = () => {
    onDisconnect(type)
  }

  const enableAutoSync = () => {
    setAutoSyncEnabled(!autoSyncEnabled)
  }

  // const handleChange = event => {
  //   const {
  //     target: { value }
  //   } = event
  //   setSelectedGroups(typeof value === 'string' ? value.split(',') : value)
  // }

  // const handleRemoveSelectedGroup = id => {
  //   setSelectedGroups(toggleElement(selectedGroups, id))
  // }

  return (
    <>
      <Dialog
        sx={{ '& .MuiPaper-root': { borderRadius: '22px', maxWidth: '750px', width: '100%' } }}
        open={open}
        // onClose={() => {
        //   clearForm()
        // }}
      >
        <DialogContent>
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                background: '#00000030',
                zIndex: '2'
              }}
              className='column column-center'
            >
              <LoadingIndicator text={'Please wait'} />
            </div>
          )}
          <div
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '14px',
              background: '#fafafa',
              padding: '20px',
              marginBottom: '15px'
            }}
          >
            <div className='row'>
              <div style={{ marginRight: 20 }}>
                <ConnectorIcon type={type} />
              </div>
              <div className='column'>
                <p style={{ fontWeight: 900, fontSize: 18, marginBottom: '6px' }}>
                  {type?.replace('AD', 'Entra ID (Azure AD)')}
                </p>
                {/* <p
                  style={{
                    fontSize: 13,
                    opacity: 0.7
                  }}
                >
                  Last sync:{' '}
                   {dayjs
                    .utc(updatedAt)
                    .local()
                    .format('DD MMM YYYY HH:mm')}
                4 hours ago
              </p> */}
              </div>
              <Spacer />
              <div className='row'>
                {type == 'AD' && (
                  <Button
                    onClick={() => {
                      if (onUpdateCredentials) {
                        onUpdateCredentials()
                      } else if (handleConnectTo) {
                        handleConnectTo('AD', true)
                      }
                    }}
                    variant='outlined'
                    sx={{
                      borderRadius: '14px',
                      color: '#6123FF',
                      borderColor: '#6123FF',
                      textTransform: 'none',
                      margin: '15px 20px 0px 0px'
                    }}
                  >
                    Update credentials
                  </Button>
                )}
                {/* <Button
                  onClick={() => { }}
                  variant='outlined'
                  sx={{
                    borderRadius: '14px',
                    color: '#6123FF',
                    borderColor: '#6123FF',
                    textTransform: 'none',
                    margin: '15px 20px 0px 0px'
                  }}
                >
                  Sync
                </Button> */}
                <Button
                  onClick={disconnect}
                  variant='outlined'
                  sx={{
                    borderRadius: '14px',
                    color: '#B40020',
                    borderColor: '#B40020',
                    textTransform: 'none',
                    margin: '15px 0px 0px 0px'
                  }}
                >
                  Disconnect
                </Button>
              </div>
            </div>
          </div>
          <div
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '14px',
              background: '#fafafa',
              padding: '20px',
              marginBottom: '15px'
            }}
          >
            <p style={{ fontWeight: 900, fontSize: 18, marginBottom: '10px' }}>Employees</p>
            <div className='row row-center'>
              <CustomCheckbox
                checked={includeType == INCLUDE_TYPES.allUsers}
                onClick={() => {
                  setIncludeType(INCLUDE_TYPES.allUsers)
                  setSelectedGroups([])
                  setSelectedOptions([])
                }}
              />
              <p>Sync all employees</p>
            </div>
            <div className='row row-center'>
              <CustomCheckbox
                checked={includeType == INCLUDE_TYPES.allUsersAndGroups}
                onClick={() => {
                  setIncludeType(INCLUDE_TYPES.allUsersAndGroups)
                  setSelectedGroups([])
                  setSelectedOptions([])
                }}
              />
              <p>Sync all employees with all groups and organizational units</p>
            </div>
            <div className='row row-center' style={{ marginBottom: 10 }}>
              <CustomCheckbox
                disabled={groups.length == 0}
                checked={includeType == INCLUDE_TYPES.onlySpecificUsers}
                onClick={() => setIncludeType(INCLUDE_TYPES.onlySpecificUsers)}
              />
              <p>Sync only employees in specific groups and organizational units</p>
            </div>

            <Autocomplete
              error={includeType == INCLUDE_TYPES.onlySpecificUsers && selectedGroups.length == 0}
              disabled={includeType !== INCLUDE_TYPES.onlySpecificUsers || groups.length == 0}
              fullWidth
              multiple
              options={groups}
              getOptionLabel={option => option.name}
              value={selectedOptions}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              onChange={(_, newValue) => {
                setSelectedOptions(newValue)
                setSelectedGroups(newValue.map(el => el.uuid))
              }}
              renderInput={params => <TextField {...params} label='Select groups' placeholder='Search...' />}
              filterSelectedOptions
              sx={{
                marginBottom: '10px',
                '& .MuiInputBase-root': {
                  borderRadius: '10px'
                },
                '& .MuiFormLabel-root.Mui-focused': {
                  color: '#6123FF'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6123FF'
                }
              }}
            />

            {includeType == INCLUDE_TYPES.onlySpecificUsers && selectedGroups.length == 0 && (
              <p style={{ marginTop: 0 }} className='error-text'>
                Please, select at least one group
              </p>
            )}
          </div>
          <div style={{ border: '1px solid #e0e0e0', borderRadius: '14px', background: '#fafafa', padding: '20px' }}>
            <p style={{ fontWeight: 900, fontSize: 18, marginBottom: '10px' }}>Auto-sync</p>
            <div style={{ marginBottom: 10, marginTop: 20 }} className='row'>
              <CyberSwitchV2 checked={autoSyncEnabled} onChange={enableAutoSync} />
              <p
                style={{
                  marginLeft: 12,
                  fontWeight: 600
                }}
              >
                Enable
              </p>
            </div>

            {autoSyncEnabled ? (
              <div className='row row-center'>
                <p>Employees are synchronized on a</p>
                <RadioSelect
                  style={{ margin: '0px 10px' }}
                  options={['Daily', 'Three-day', 'Weekly', 'Monthly']}
                  value={autoSyncBasis}
                  onSelectOption={changeSyncBasis}
                />
                <p>basis</p>
              </div>
            ) : (
              <p>Syncronization of new employees/groups is done manually</p>
            )}
          </div>
        </DialogContent>
        <Divider />
        <div className='row'>
          <Button
            disabled={loading}
            onClick={() => {
              clearForm()
            }}
            sx={{
              height: '50px',
              textTransform: 'none',
              borderRadius: '14px',
              color: '#6123FF',
              borderColor: '#6123FF',
              flex: '1',
              margin: '24px 16px 24px 32px'
            }}
            variant={'outlined'}
          >
            Cancel
          </Button>
          <Button
            disabled={
              (includeType == INCLUDE_TYPES.onlySpecificUsers && selectedGroups.length === 0) || loading || isCooldown
            }
            onClick={handleSave}
            sx={{
              height: '50px',
              textTransform: 'none',
              borderRadius: '14px',
              backgroundColor: '#6123FF',
              flex: '1',
              margin: '24px 32px 24px 0px'
            }}
            variant={'contained'}
          >
            {isCooldown ? `Sync (${cooldown.time}s)` : 'Sync'}
          </Button>
        </div>
      </Dialog>
    </>
  )
}

export default ConfigureDialog
